/**
 * 환경 변수 관리
 *
 * Vite의 import.meta.env를 사용하여 환경 변수를 타입 안전하게 관리합니다.
 * 모든 환경 변수는 VITE_ 접두사가 필요합니다.
 */

import { STORAGE_KEYS } from './constants'

interface Env {
  // Google OAuth
  GOOGLE_CLIENT_ID: string // Required

  // API 설정
  API_BASE_URL: string

  // 환경
  ENV: 'development' | 'production'

  // Repository 모드 (Mock vs 실제 연동)
  USE_MOCK_DATA: boolean

  // 초기화 모드 (Test vs Production)
  INIT_MODE: 'test' | 'production'
}

// Note: SPREADSHEET_ID removed - now managed via workspace system
// See: src/core/workspace/workspaceStore.ts

/**
 * 환경 변수 검증 및 추출
 *
 * 필수 환경 변수가 없으면 에러를 발생시킵니다.
 */
function getEnv(): Env {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  const apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL || 'https://sheets.googleapis.com/v4'
  const env = import.meta.env.VITE_ENV || 'development'
  const initMode = import.meta.env.VITE_INIT_MODE || 'production'

  // REQUIRED: Google Client ID must be set
  if (!clientId) {
    throw new Error(
      'VITE_GOOGLE_CLIENT_ID is required. Please check your .env file.\n' +
        'Get your client ID from Google Cloud Console: https://console.cloud.google.com/apis/credentials'
    )
  }

  // Mock 데이터 사용 여부
  // Production 환경에서는 강제로 비활성화
  let useMockData = false

  if (env === 'production') {
    // Production 환경에서는 항상 false (Vercel 환경 변수로도 강제)
    useMockData = false

    // 모바일 환경 대응: localStorage 정리
    // 개발 중 localStorage에 mock 데이터가 저장되었을 수 있으므로 제거
    // @why env.ts는 logger보다 먼저 평가될 수 있으므로 console.* 직접 사용을 유지.
    if (localStorage.getItem(STORAGE_KEYS.USE_MOCK_DATA) !== null) {
      console.warn('⚠️ Production 환경에서는 Mock 모드를 사용할 수 없습니다.')
      localStorage.removeItem(STORAGE_KEYS.USE_MOCK_DATA)
    }
    // Mock 유저 정보도 제거
    const userInfo = localStorage.getItem(STORAGE_KEYS.LEGACY_USER_INFO)
    if (userInfo && userInfo.includes('mock')) {
      console.warn('⚠️ Mock 유저 정보를 제거합니다.')
      localStorage.removeItem(STORAGE_KEYS.LEGACY_USER_INFO)
    }
  } else {
    // Development 환경에서만 mock 모드 활성화 가능
    // 1순위: localStorage (사용자 설정)
    // 2순위: 환경 변수
    const localStorageMockMode = localStorage.getItem(STORAGE_KEYS.USE_MOCK_DATA)
    useMockData =
      localStorageMockMode !== null
        ? localStorageMockMode === 'true'
        : import.meta.env.VITE_USE_MOCK_DATA === 'true'
  }

  return {
    GOOGLE_CLIENT_ID: clientId,
    API_BASE_URL: apiBaseUrl,
    ENV: env as 'development' | 'production',
    USE_MOCK_DATA: useMockData,
    INIT_MODE: initMode as 'test' | 'production',
  }
}

/**
 * 환경 변수 객체
 *
 * 애플리케이션 전역에서 사용됩니다.
 */
export const env = getEnv()

/**
 * 현재 Mock 모드인지 확인
 */
export const isMockMode = () => env.USE_MOCK_DATA

/**
 * 개발 모드인지 확인
 */
export const isDevelopment = () => env.ENV === 'development'

/**
 * 프로덕션 모드인지 확인
 */
export const isProduction = () => env.ENV === 'production'

/**
 * Mock 모드 전환
 *
 * localStorage에 Mock 모드 설정을 저장하고 페이지를 새로고침합니다.
 * 새로고침 시 getEnv()가 다시 호출되며 localStorage 값을 읽어 적용됩니다.
 */
export function toggleMockMode() {
  const currentMode = isMockMode()
  const newMode = !currentMode

  // localStorage에 저장
  localStorage.setItem(STORAGE_KEYS.USE_MOCK_DATA, newMode.toString())

  // 페이지 새로고침하여 변경사항 적용
  window.location.reload()
}
